import moment from 'moment';
import Label from '@labels/models/label';
import { AVAILABLE_ROLES_BY_TYPE } from '@projectMembers/models/projectRole';
import { nullRolePermissions } from '@projectMembers/models/projectRolePermissions';

import GuestHash from '@projects/models/guestHash';
import ImmutableEntity from './immutableEntity';
import { OrganizerType } from './folder';
import User from './user';

export const PROJECT_STATUS_HIDDEN = 'archived';
export const PROJECT_STATUS_ACTIVE = 'active';

const ifSet = ( value, defaultValue ) => ( value !== undefined ? value : defaultValue );

export default class Project extends ImmutableEntity {
	/**
	 * @param {number} id Project id
	 * @param {string} name display name
	 * @param {string} description
	 * @param {string} clientName
	 * @param {string} primaryContact
	 * @param {string} coverImageURL
	 * @param {number} coverAssetID asset that contains the cover image url of the project
	 * @param {'active'|'archived'} status
	 * @param {string} shareHash
	 * @param {number} unresolvedCommentsCount
	 * @param {boolean} hasRushPriority
	 * @param {object} owner
	 * @param {'admin'|'user'} currentRoleId
	 * @param {Date} startDate
	 * @param {Date} dueDate
	 * @param {Date} createdAt
	 * @param {Date} updatedAt
	 * @param {Workspace} workspace
	 * @param {object} guestHash
	 * @param {boolean} notificationsMuted
	 */
	constructor(
		id,
		name,
		description,
		clientName,
		primaryContact,
		coverImageURL,
		coverAssetID,
		status,
		shareHash,
		shareHashDownloadable,
		unresolvedCommentsCount,
		hasRushPriority,
		currentRoleId,
		startDate,
		dueDate,
		createdAt,
		updatedAt,
		workspace,
		lastActivityAt,
		labels = [],
		label,
		storageUsedInBytes,
		totalAssetsCount,
		lastModifier,
		owner,
		guestHashes,
		notificationsMuted,
		unresolvedMentionsCount,
		downloadsEnabledForReviewers,
		seePastCommentsEnabledForReviewers,
		supportedFeatures,
		assetsStartRetouched,
		hasComments
	) {
		super();

		this.id = id;
		this.name = name;
		this.description = description;
		this.clientName = clientName;
		this.primaryContact = primaryContact;
		this.coverImageURL = coverImageURL;
		this.coverAssetID = coverAssetID;
		this.status = status;
		this.shareHash = shareHash;
		this.shareHashDownloadable = shareHashDownloadable;
		this.unresolvedCommentsCount = unresolvedCommentsCount;
		this.hasRushPriority = hasRushPriority;
		this.currentRoleId = currentRoleId || '-';
		this.startDate = startDate;
		this.dueDate = dueDate;
		this.createdAt = createdAt;
		this.updatedAt = updatedAt;
		this.workspace = workspace;
		this.lastActivityAt = lastActivityAt;
		this.labels = labels;
		this.label = label;
		this.storageUsedInBytes = storageUsedInBytes;
		this.totalAssetsCount = totalAssetsCount;
		this.lastModifier = lastModifier;
		this.owner = owner;
		this.guestHashes = guestHashes;
		this.notificationsMuted = notificationsMuted;
		this.unresolvedMentionsCount = unresolvedMentionsCount || 0;
		this.downloadsEnabledForReviewers = ifSet( downloadsEnabledForReviewers, false );
		this.seePastCommentsEnabledForReviewers = ifSet( seePastCommentsEnabledForReviewers, true );
		this.supportedFeatures = supportedFeatures || [];
		this.assetsStartRetouched = assetsStartRetouched;
		this.hasComments = hasComments;
	}

	get allowsDownloads() {
		return true;
	}

	get downloadsEnabled() {
		return this.currentUserRole && ( !this.currentUserRole.isReviewer || this.downloadsEnabledForReviewers );
	}

	get hasNotificationsMuted() {
		return this.notificationsMuted;
	}

	get hasActiveWorkspace() {
		return this.workspace.isActive;
	}

	get isOnExpiredFreeTrial() {
		return this.workspace.isOnExpiredFreeTrial;
	}

	get workspaceID() {
		return this.workspace.id;
	}

	get labellableID() {
		return this.workspaceID;
	}

	get availableLabels() { return this.labels; }

	get currentUserRole() {
		return AVAILABLE_ROLES_BY_TYPE[ this.currentRoleId ];
	}

	get currentUserIsOwner() {
		return this.currentUserRole?.isOwner;
	}

	get currentUserIsMember() {
		return this.currentRoleId !== '-';
	}

	get userPermissions() { return this.currentUserRole?.permissions || nullRolePermissions; }

	get hasUnresolvedComments() { return this.unresolvedCommentsCount > 0; }

	get currentUserIsPart() { return !!this.currentUserRole; }

	get labelAssignedType() { return Label.type.workspace; }

	get hasAnyLabel() {
		return !!this.label;
	}

	get formattedStoragedUsed() {
		return this.storageUsedInBytes.formatBytes();
	}

	get createdAtToDisplay() {
		return this._formatDateIfPresent( this.createdAt, 'MM/DD/YY' );
	}

	get dueDateToDisplay() {
		return this._formatDateIfPresent( this.dueDate, 'MM/DD/YY' );
	}

	get lastActivityToDisplay() {
		return this._formatDateIfPresent( this.lastActivityAt, 'MM/DD, h:mmA' );
	}

	get assetsCount() { return this.totalAssetsCount; }

	get url() {
		return `/projects/${this.id}`;
	}

	get projectDashboardUrl() {
		return this.isHidden ? '/hidden_projects' : '/projects';
	}

	get type() {
		return OrganizerType.project;
	}

	get isHidden() {
		return this.status === PROJECT_STATUS_HIDDEN;
	}

	get currentUserCanSeeCommentMetrics() {
		return !this.currentUserIsReviewer || this.seePastCommentsEnabledForReviewers;
	}

	get currentUserIsReviewer() {
		return this.currentUserRole?.isReviewer || false;
	}

	hasLabel( label ) {
		return !!this.label && !!label && this.label.id === label.id;
	}

	fetchAssetsWith( system ) {
		return system.fetchAssetsForProject( this );
	}

	async fetchFoldersWith( system ) {
		return system.fetchFoldersForProject( this );
	}

	async fetchOverviewWith( system ) {
		return system.fetchOverviewForProject( this );
	}

	async fetchInformationWith( system ) {
		return system.fetchInformationForProject( this );
	}

	// Serialization
	static fromJSON( properties, workspace, labels ) {
		return new Project(
			properties.id,
			properties.name,
			properties.description,
			properties.client_name,
			properties.primary_contact,
			properties.cover_url,
			properties.cover_id,
			properties.status,
			properties.share_hash,
			properties.share_hash_downloadable,
			properties.unresolved_comments_count,
			properties.rush_priority,
			properties.current_user_role.type,
			properties.start_date ? new Date( properties.start_date ) : null,
			properties.due_date ? new Date( properties.due_date ) : null,
			new Date( properties.created_at ),
			new Date( properties.updated_at ),
			workspace,
			new Date( properties.last_activity_at ),
			labels,
			properties.label && Label.fromJSON( properties.label ),
			properties.storage_used_in_bytes,
			properties.total_assets_count,
			properties.last_activity_by && User.fromJSON( properties.last_activity_by ),
			properties.owner && User.fromJSON( properties.owner ),
			properties.guest_hashes && properties.guest_hashes.map( GuestHash.fromJSON ),
			properties.notifications_muted,
			properties.unresolved_mentions_count,
			ifSet( properties.downloads_enabled_for_reviewers, false ),
			ifSet( properties.see_past_comments_enabled_for_reviewers, true ),
			properties.supported_features,
			properties.assets_start_retouched,
			ifSet( properties.has_comments, false )
		);
	}

	shareLink( { withDownloads } ) {
		const shareHash = withDownloads ? this.shareHashDownloadable : this.shareHash;
		return `projects/shared_view/${shareHash}`;
	}

	get isProject() {
		return true;
	}

	get isFolder() {
		return false;
	}

	get selectionID() {
		return `project-${this.id}`;
	}

	_formatDateIfPresent( date, format ) {
		return date ? moment( date ).format( format ) : '';
	}
}
